import React from 'react';
import ReactDOM from 'react-dom';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectSelectionMode } from '../../Store/tileSelectionSlice';
import { useFetchResultQuery } from '../../api/result';
import LoadingSpiner from '../Loader/LoadingSpiner';
import { FaXmark } from 'react-icons/fa6';
import { useFetchImageQuery } from '../../api/image';
import ResultViewerImageToolBar from './ResultViewerImageToolBar';
import ResultViewerSidebar from './ResultViewerSidebar';
import ImageViewer from './ImageViewer';
import './ResultView.css';
import classNames from 'classnames';
import TileSelector from './TileSelector/TIleSelector';

// Assuming the shape of your result data
interface ResultData {
    imageName: string;
    resultName: string;
    analysisData: string;
    imageUrl: string;
}

// Props type definition
interface AnalysisModalProps {
    projectId: string;
    imageId: string;
    resultId: string;
    onClose: () => void;
}

const ResultView: React.FC<AnalysisModalProps> = ({ projectId, imageId, resultId, onClose }) => {
    const { compare } = useParams<{ compare?: string }>();
    const [searchParams] = useSearchParams();
    const dataIndex = parseInt(searchParams.get('dataIndex') || '0');

    const { data: result, isLoading, isError } = useFetchResultQuery(resultId);
    const { data: imageData, isLoading: imageIsLoading, isError: imageIsError } = useFetchImageQuery(imageId);

    const selectionModeOn = useSelector(selectSelectionMode);
    const [showSidebar, setShowSidebar] = React.useState(true);
    const [showResultList, setShowResultList] = React.useState(true);

    const handleToggleSidebar = () => {
        setShowSidebar(!showSidebar);
    }

    const handleToggleResultList = () => {
        setShowResultList(!showResultList);
    }

    const loading = isLoading || imageIsLoading;
    const error = isError || imageIsError;

    if (loading) return <LoadingSpiner />;
    if (error) return <p>Error loading result</p>;
    if (!result) return null;
    const contentSpan = showSidebar ? 'col-span-10' : 'col-span-12';

    const imageContainerClass = classNames({
        compare: !!compare
    }, '__image-content')

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 bg-white dark:bg-neutral-600">
            <div className="h-full flex flex-col">
                <div className="__result-toolbar flex py-4 px-2 justify-between items-center bg-neutral-200 dark:bg-neutral-800 border-b border-y-neutral-300 dark:border-y-neutral-600">
                    <div className="flex content-center items-center">
                        <button onClick={onClose} className="text-lg rounded-none hover:bg-red-700 hover:text-neutral-100 hover:dark:bg-red-700 hover:dark:text-neutral-100 rounded w-12 h-12 text-center align-center flex content-center justify-center items-center"><FaXmark className='' /></button>
                        <button className='hover:bg-neutral-300 hover:dark:bg-neutral-700 py-2 px-4 rounded'>
                            <h2 className="text-md">{imageData.name}</h2>
                        </button>
                        {/* <div className='text-lg ml-2 mr-4'>/</div> */}
                        {/* <h1 className="text-lg font-bold">{result.name}</h1> */}
                    </div>
                </div>
                <div className="flex-1 grid grid-cols-12 min-h-0">
                    <div className={`${contentSpan} bg-neutral-100 dark:bg-neutral-700 min-h-0`}>
                        <div className="h-full flex flex-col">
                            <div className='__image-toolbar'>
                                <ResultViewerImageToolBar
                                    handleToggleSidebar={handleToggleSidebar}
                                    handleToggleResultList={handleToggleResultList}
                                    sidebarOpen={showSidebar}
                                    resultListOpen={showResultList}
                                    showCompareButton={!compare} />
                            </div>
                            <div className={`flex-1 min-h-0 ${imageContainerClass}`}>
                                <ImageViewer 
                                    imageId={imageId} 
                                    resultId={resultId} 
                                    compare={compare}
                                    showResultList={showResultList}
                                    onToggleResultList={handleToggleResultList} />
                            </div>
                        </div>
                    </div>
                    { !selectionModeOn && showSidebar && 
                        <div className='col-span-2 bg-neutral-200 dark:bg-neutral-800 border-r border-r-neutral-300 dark:border-r-neutral-600 min-h-0'>
                            <ResultViewerSidebar resultId={resultId} compareId={compare} dataIndex={dataIndex}/>
                        </div> }
                    { selectionModeOn && <TileSelector /> }
                </div>
            </div>
        </div>,
        document.getElementById('image-viewer')!
    );
};

export default ResultView;
